import React from 'react';
import { Link } from 'react-router-dom';
import {
  Home, Search, ScrollText, BookOpen, Compass, MapPin, ArrowRight,
} from 'lucide-react';
import { FortIcon, TempleIcon } from '../icons/HeritageIcons';
import Footer from '../Footer';
import SEOOptimizer from '../Performance/SEOOptimizer';
import { useTranslation } from '../../contexts/TranslationContext';

const HERITAGE_PATHS = [
  {
    to: '/forts',
    icon: FortIcon,
    titleKey: 'errors.pathForts',
    descKey: 'errors.pathFortsDesc',
    accent: 'from-[#3d2818] to-[#5c3d1a]',
  },
  {
    to: '/temples',
    icon: TempleIcon,
    titleKey: 'errors.pathTemples',
    descKey: 'errors.pathTemplesDesc',
    accent: 'from-amber-900 to-amber-800',
  },
  {
    to: '/heritage',
    icon: Compass,
    titleKey: 'errors.pathHeritage',
    descKey: 'errors.pathHeritageDesc',
    accent: 'from-stone-800 to-stone-700',
  },
  {
    to: '/history',
    icon: ScrollText,
    titleKey: 'errors.pathHistory',
    descKey: 'errors.pathHistoryDesc',
    accent: 'from-[#4a3020] to-[#2a1810]',
  },
  {
    to: '/books',
    icon: BookOpen,
    titleKey: 'errors.pathBooks',
    descKey: 'errors.pathBooksDesc',
    accent: 'from-indigo-950 to-indigo-900',
  },
  {
    to: '/search',
    icon: Search,
    titleKey: 'errors.searchSite',
    descKey: 'errors.pathSearchDesc',
    accent: 'from-amber-950 to-[#2a1810]',
  },
];

export default function Error404() {
  const { t, currentLanguage } = useTranslation();

  const suggestions = [
    'errors.suggestion1',
    'errors.suggestion2',
    'errors.suggestion3',
    'errors.suggestion4',
  ];

  return (
    <div className="min-h-screen bg-[#faf6ef]" lang={currentLanguage === 'mr' ? 'mr' : 'en'}>
      <SEOOptimizer
        title={t('errors.seoTitle404')}
        description={t('errors.notFoundMessage')}
        keywords="404, gadkot, maharashtra heritage, forts"
        url={typeof window !== 'undefined' ? window.location.href : 'https://gadkot.com/404'}
        type="ErrorPage"
      />

      {/* Hero — lost on the Sahyadri trail */}
      <section className="hero-maharashtra relative overflow-hidden text-[#f5ebe0]">
        <div className="hero-maharashtra__sky absolute inset-0" aria-hidden />
        <div className="hero-maharashtra__heritage-wash absolute inset-0" aria-hidden />
        <div className="hero-maharashtra__vignette absolute inset-0" aria-hidden />
        <div className="error-404-watermark pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden>
          <span>404</span>
        </div>

        <div className="relative z-10 container mx-auto max-w-4xl px-4 py-16 text-center md:py-24">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-amber-500/45 bg-[#2a1810]/85 px-4 py-1.5">
            <MapPin className="h-4 w-4 text-amber-400" />
            <span className="font-marathi text-sm text-amber-100">{t('errors.notFoundBadge')}</span>
          </div>

          <FortIcon className="mx-auto mb-4 h-12 w-12 text-amber-400/90 md:h-14 md:w-14" />

          <h1 className="font-marathi-display text-3xl font-bold tracking-tight md:text-5xl">
            {t('errors.notFoundTitle')}
          </h1>

          <p className="font-marathi mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[#e8d5bc] md:text-lg">
            {t('errors.notFoundMessage')}
          </p>

          <blockquote className="error-404-scroll font-marathi mx-auto mt-8 max-w-xl text-left text-sm leading-relaxed text-[#d4bc9a] md:text-base">
            <p className="italic">&ldquo;{t('errors.notFoundQuote')}&rdquo;</p>
            <footer className="mt-3 text-xs font-medium not-italic text-amber-300/80">
              {t('errors.notFoundQuoteAttr')}
            </footer>
          </blockquote>

          <p className="font-marathi mx-auto mt-6 max-w-2xl text-sm text-[#c9a86c] md:text-base">
            {t('errors.notFoundStory')}
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/"
              className="font-marathi inline-flex items-center gap-2 rounded-lg border border-amber-400/50 bg-amber-500/15 px-6 py-3 text-sm font-semibold text-amber-50 transition hover:bg-amber-500/25"
            >
              <Home className="h-4 w-4" />
              {t('errors.backToSafety')}
            </Link>
            <Link
              to="/search"
              className="font-marathi inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-700 to-amber-900 px-6 py-3 text-sm font-semibold text-amber-50 shadow-md transition hover:from-amber-800 hover:to-amber-950"
            >
              <Search className="h-4 w-4" />
              {t('errors.searchSite')}
            </Link>
          </div>
        </div>
      </section>

      <main className="container mx-auto max-w-6xl px-4 py-12 md:py-16">
        <div className="mb-10 text-center">
          <div className="hero-ornament-line mx-auto mb-4 w-28 md:w-36" />
          <h2 className="font-marathi-display text-2xl font-bold text-[#2a1810] md:text-3xl">
            {t('errors.heritagePaths')}
          </h2>
          <p className="font-marathi mt-2 text-sm text-[#5c4a3a] md:text-base">{t('errors.continueJourney')}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {HERITAGE_PATHS.map(({ to, icon: Icon, titleKey, descKey, accent }) => (
            <Link
              key={to}
              to={to}
              className="search-result-card group flex min-w-0 gap-4 overflow-hidden rounded-xl p-4 transition hover:-translate-y-0.5"
            >
              <div
                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-amber-100 shadow-inner`}
              >
                <Icon className="h-7 w-7" strokeWidth={1.65} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-marathi text-base font-bold text-[#2a1810] group-hover:text-amber-900">
                  {t(titleKey)}
                </h3>
                <p className="font-marathi mt-1 line-clamp-2 text-xs text-[#5c4a3a]">{t(descKey)}</p>
                <span className="font-marathi mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-800">
                  {t('errors.exploreNow')} <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <div className="trek-stat-card rounded-2xl p-6 md:p-8">
            <h3 className="font-marathi-display flex items-center gap-2 text-xl font-bold text-[#2a1810]">
              <Compass className="h-5 w-5 text-amber-800" />
              {t('errors.whatNext')}
            </h3>
            <ol className="font-marathi mt-5 space-y-4">
              {suggestions.map((key, index) => (
                <li key={key} className="flex gap-3 text-sm text-[#5c4a3a] md:text-base">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-amber-400/50 bg-amber-50 text-xs font-bold text-amber-900">
                    {index + 1}
                  </span>
                  <span className="pt-0.5">{t(key)}</span>
                </li>
              ))}
            </ol>
            <div className="font-marathi mt-6 rounded-xl border border-amber-300/40 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
              <strong className="text-amber-900">{t('errors.tipLabel')}</strong> {t('errors.tipText')}
            </div>
          </div>

          <div className="trek-stat-card flex flex-col rounded-2xl p-6 md:p-8">
            <h3 className="font-marathi-display text-xl font-bold text-[#2a1810]">{t('errors.needHelp')}</h3>
            <p className="font-marathi mt-3 flex-1 text-sm leading-relaxed text-[#5c4a3a] md:text-base">
              {t('errors.needHelpText')}
            </p>
            <p className="font-marathi mt-4 text-sm italic text-[#7a6555]">{t('errors.heritageClosing')}</p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="font-marathi inline-flex flex-1 items-center justify-center rounded-lg border border-amber-400/45 bg-white px-4 py-2.5 text-sm font-semibold text-amber-950 transition hover:bg-amber-50"
              >
                {t('errors.contactSupport')}
              </Link>
              <Link
                to="/about"
                className="font-marathi inline-flex flex-1 items-center justify-center rounded-lg bg-gradient-to-r from-amber-800 to-[#2a1810] px-4 py-2.5 text-sm font-semibold text-amber-50 transition hover:from-amber-900"
              >
                {t('errors.aboutGadkot')}
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
